<template>
  <div>
    <!-- Подбор в Google Images: клик по постеру — модалка с ретушью, «Сохранить» кладёт его на S3 (movie_media, minimal_poster) -->
    <div class="my-1 py-25">
      <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem;">
        <b-button
            variant="outline-primary"
            size="sm"
            :disabled="isSearching"
            @click="search"
        >
          <b-spinner v-if="isSearching" small class="mr-50" />
          <feather-icon v-else icon="SearchIcon" class="mr-50" />
          <span>Подобрать</span>
        </b-button>
        <b-button v-if="results.length" variant="flat-secondary" size="sm" @click="closeResults">
          <feather-icon icon="XIcon" class="mr-50" />
          <span>Скрыть</span>
        </b-button>
      </div>

      <div v-if="isSearched && !isSearching && !results.length" class="text-muted small mt-1">
        Ничего не найдено
      </div>

      <div v-if="results.length" class="search-grid mt-1">
        <div
            v-for="result in results"
            :key="result.url"
            class="search-item"
            :class="{ 'search-item-exists': result.exists }"
            :title="result.title || ''"
            @click="openPreview(result)"
        >
          <b-img :src="result.preview_url || result.url" class="search-img" loading="lazy" />
          <b-badge v-if="result.exists" variant="success" class="search-badge">Добавлен</b-badge>
          <div v-if="result.width && result.height" class="small text-muted">
            {{ result.width }}×{{ result.height }}
          </div>
        </div>
      </div>

      <div v-if="results.length && nextPage" class="mt-1 text-center">
        <b-button
            variant="outline-secondary"
            size="sm"
            :disabled="isLoadingMore"
            @click="loadMore"
        >
          <b-spinner v-if="isLoadingMore" small class="mr-50" />
          <feather-icon v-else icon="ChevronsDownIcon" class="mr-50" />
          <span>Показать ещё</span>
        </b-button>
      </div>
    </div>

    <!-- Превью найденного постера: как модалка обложки — ретушь и сохранение -->
    <b-modal
        v-model="isPreviewVisible"
        :title="previewResult && previewResult.title ? previewResult.title : 'Минимал постер'"
        size="lg"
        centered
        hide-footer
        @hidden="previewResult = null"
    >
      <div v-if="previewResult">
        <image-inpainting-editor
            ref="editor"
            :image-url="previewResult.url"
            @result="editedImageUrl = $event"
            @processing="isEditorProcessing = $event"
        />
        <div v-if="previewResult.exists" class="text-warning small mt-50">
          Этот постер уже добавлен — сохранение создаст ещё одну копию
        </div>
        <div class="mt-1 d-flex" style="gap: 0.5rem;">
          <b-button
              v-if="editedImageUrl && $can('manage', 'all')"
              variant="outline-secondary"
              @click="$refs.editor.resetToOriginal()"
          >
            <feather-icon icon="RotateCcwIcon" class="mr-50" />
            Вернуть оригинал
          </b-button>
          <b-button
              variant="success"
              :disabled="isSaving || isEditorProcessing"
              @click="savePreview"
          >
            <b-spinner v-if="isSaving" small class="mr-50" />
            <feather-icon v-else icon="DownloadIcon" class="mr-50" />
            <span class="align-middle">{{ isSaving ? 'Сохранение...' : 'Сохранить' }}</span>
          </b-button>
          <b-link
              :href="previewResult.url"
              target="_blank"
              rel="noopener noreferrer"
              class="ml-auto align-self-center"
          >
            <feather-icon icon="ExternalLinkIcon" class="mr-25" />
            <span class="align-middle">Полный размер</span>
            <span v-if="previewResult.width && previewResult.height" class="text-muted align-middle">
              ({{ previewResult.width }}×{{ previewResult.height }})
            </span>
          </b-link>
        </div>
      </div>
    </b-modal>

    <movie-media-tab
        ref="mediaTab"
        :movie-id="movieId"
        category="minimal_poster"
        :sections="[{type: 'image', title: 'Минимал постеры'}]"
        editable-images
    />
  </div>
</template>

<script>
import {
  BButton,
  BSpinner,
  BImg,
  BBadge,
  BModal,
  BLink,
} from 'bootstrap-vue'
import store from '@/store'
import { ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import MovieMediaTab from '@/views/database/MovieMediaTab.vue'
import ImageInpaintingEditor from '@/views/db-music/ImageInpaintingEditor.vue'

export default {
  name: 'MovieMinimalPostersTab',
  components: {
    BButton,
    BSpinner,
    BImg,
    BBadge,
    BModal,
    BLink,
    MovieMediaTab,
    ImageInpaintingEditor,
  },
  props: {
    movieId: { type: [Number, String], required: true },
  },
  setup(props) {
    const toast = useToast()

    const mediaTab = ref(null)
    const results = ref([])
    const isSearching = ref(false)
    const isSearched = ref(false)
    const nextPage = ref(null)
    const isLoadingMore = ref(false)

    const isPreviewVisible = ref(false)
    const previewResult = ref(null)
    const editedImageUrl = ref(null)
    const isEditorProcessing = ref(false)
    const isSaving = ref(false)

    const showToast = (title, variant, icon) => {
      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    const search = () => {
      isSearching.value = true
      store.dispatch('movies/searchMovieMinimalPosters', { id: props.movieId })
          .then(response => {
            results.value = response.data.data
            nextPage.value = response.data.next_page
            isSearched.value = true
          })
          .catch(error => {
            const message = error.response?.data?.message
            showToast(message ? `Ошибка поиска: ${message}` : 'Ошибка поиска постеров', 'danger', 'AlertTriangleIcon')
          })
          .finally(() => {
            isSearching.value = false
          })
    }

    // Следующая страница выдачи Google; дубликаты по url отбрасываем — страницы могут пересекаться
    const loadMore = () => {
      isLoadingMore.value = true
      store.dispatch('movies/searchMovieMinimalPosters', { id: props.movieId, page: nextPage.value })
          .then(response => {
            const knownUrls = new Set(results.value.map(result => result.url))
            const newResults = response.data.data.filter(result => !knownUrls.has(result.url))
            results.value = [...results.value, ...newResults]
            nextPage.value = response.data.next_page
            if (!newResults.length) {
              showToast('Новых постеров не нашлось', 'info', 'InfoIcon')
            }
          })
          .catch(error => {
            const message = error.response?.data?.message
            showToast(message ? `Ошибка поиска: ${message}` : 'Ошибка поиска постеров', 'danger', 'AlertTriangleIcon')
          })
          .finally(() => {
            isLoadingMore.value = false
          })
    }

    const closeResults = () => {
      results.value = []
      nextPage.value = null
      isSearched.value = false
    }

    const openPreview = result => {
      previewResult.value = result
      editedImageUrl.value = null
      isEditorProcessing.value = false
      isPreviewVisible.value = true
    }

    // Сохраняем отретушированную версию, если она есть, иначе оригинал; source_url — всегда исходная ссылка
    const savePreview = () => {
      const result = previewResult.value
      isSaving.value = true
      store.dispatch('movies/addMovieMediaFromUrl', {
        id: props.movieId,
        category: 'minimal_poster',
        url: editedImageUrl.value || result.url,
        sourceUrl: result.url,
      })
          .then(response => {
            mediaTab.value.appendItems([response.data.data])
            result.exists = true
            isPreviewVisible.value = false
            showToast('Постер сохранён', 'success', 'CheckIcon')
          })
          .catch(error => {
            const message = error.response?.data?.message
            showToast(message || 'Ошибка при сохранении постера', 'danger', 'AlertTriangleIcon')
          })
          .finally(() => {
            isSaving.value = false
          })
    }

    return {
      mediaTab,
      results,
      isSearching,
      isSearched,
      nextPage,
      isLoadingMore,
      isPreviewVisible,
      previewResult,
      editedImageUrl,
      isEditorProcessing,
      isSaving,
      search,
      loadMore,
      closeResults,
      openPreview,
      savePreview,
    }
  },
}
</script>

<style lang="scss" scoped>
.search-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 0.75rem;
}
.search-item {
  position: relative;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 0.428rem;
  padding: 2px;

  &:hover { border-color: #7367f0; }
}
.search-item-exists { opacity: 0.55; }
.search-img { width: 100%; height: 190px; object-fit: cover; border-radius: 0.357rem; }
.search-badge { position: absolute; top: 6px; left: 6px; }
</style>
